import { useState, useEffect, useRef } from 'react'
import { AlertTriangle } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@shared/components/ui/dialog'
import { Button } from '@shared/components/ui/button'
import { cn } from '@shared/lib/utils'
import { DialogResultTransition, type DialogStatus } from '@shared/components/ui/DialogResultTransition'
import type { ChargebackResponseFormData, ChargebackAction } from '../types'

interface ChargebackResponseFormProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: ChargebackResponseFormData) => Promise<void> | void
  onSuccess?: () => void
  /** Chargeback amount display string, e.g. "$4,235.00" */
  chargebackAmount?: string
  /** Due date for responding */
  responseDueBy?: string
}

type FightReason = { value: string; label: string }

const FIGHT_REASONS: FightReason[] = [
  { value: 'goods_delivered',     label: 'Goods delivered' },
  { value: 'service_rendered',    label: 'Service rendered' },
  { value: 'not_fraudulent',      label: 'Not fraudulent' },
  { value: 'duplicate_dispute',   label: 'Duplicate dispute' },
  { value: 'other',               label: 'Other' },
]

const AUTO_CLOSE_MS = 1500

const ACTION_OPTIONS: Array<{ value: ChargebackAction; title: string; description: string }> = [
  {
    value: 'fight',
    title: 'Fight chargeback',
    description: 'Submit evidence to dispute the chargeback claim',
  },
  {
    value: 'accept',
    title: 'Accept chargeback',
    description: 'Funds will be returned to the cardholder',
  },
]

export function ChargebackResponseForm({
  open,
  onClose,
  onSubmit,
  onSuccess,
  chargebackAmount,
  responseDueBy,
}: ChargebackResponseFormProps) {
  const [action, setAction]                     = useState<ChargebackAction>('fight')
  const [fightReason, setFightReason]           = useState('')
  const [evidenceDescription, setEvidenceDesc]  = useState('')
  const [notes, setNotes]                       = useState('')
  const [isLoading, setIsLoading]               = useState(false)
  const [status, setStatus]                     = useState<DialogStatus>('idle')
  const timerRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    if (open) {
      setAction('fight')
      setFightReason('')
      setEvidenceDesc('')
      setNotes('')
      setIsLoading(false)
      setStatus('idle')
    }
    return () => clearTimeout(timerRef.current)
  }, [open])

  const isValid =
    action === 'accept' ||
    (action === 'fight' && fightReason.length > 0)

  const handleSubmit = async () => {
    if (!isValid || isLoading) return
    setIsLoading(true)
    try {
      await onSubmit({
        action,
        fightReason: action === 'fight' ? fightReason : undefined,
        evidenceDescription: action === 'fight' && evidenceDescription.trim()
          ? evidenceDescription.trim()
          : undefined,
        notes: notes.trim(),
      })
      setStatus('success')
      timerRef.current = setTimeout(() => {
        onSuccess?.()
        onClose()
      }, AUTO_CLOSE_MS)
    } catch {
      setStatus('error')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && status !== 'success') onClose() }}>
      <DialogContent className="max-w-[480px] gap-0 p-0 overflow-hidden">
        <DialogResultTransition
          status={status}
          successMessage={action === 'fight' ? 'Chargeback disputed' : 'Chargeback accepted'}
          errorMessage="Failed to submit response"
          errorDescription="Something went wrong. Please try again."
          onRetry={() => setStatus('idle')}
          onClose={onClose}
        >

        {/* ── Header ── */}
        <DialogHeader className="px-6 pt-6 pb-0">
          <DialogTitle className="text-[17px] font-semibold leading-tight">
            Respond to chargeback
          </DialogTitle>
        </DialogHeader>

        {/* ── Scrollable body ── */}
        <div className="px-6 pt-4 pb-6 space-y-5 overflow-y-auto max-h-[calc(100vh-160px)]">

          {/* Warning banner */}
          <div className="flex gap-3 rounded-xl bg-status-warning-bg p-3.5">
            <AlertTriangle className="h-4 w-4 text-status-warning-text mt-0.5 shrink-0" />
            <div className="text-[13px] leading-[1.45] text-status-warning-text">
              {chargebackAmount && (
                <p className="font-medium mb-0.5">Chargeback amount: {chargebackAmount}</p>
              )}
              <p>
                {responseDueBy
                  ? `Response required by ${responseDueBy}. `
                  : ''}
                If accepted, the funds will be returned to the cardholder. Fighting requires sufficient evidence.
              </p>
            </div>
          </div>

          {/* Action selector */}
          <div className="space-y-2">
            <label className="text-[13px] font-medium text-foreground">
              How would you like to respond?
            </label>
            <div className="rounded-xl border border-input overflow-hidden divide-y divide-input">
              {ACTION_OPTIONS.map(({ value, title, description }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setAction(value)}
                  className={cn(
                    'w-full flex items-center gap-3 px-4 py-3.5 text-left transition-colors',
                    action === value ? 'bg-card' : 'bg-card hover:bg-muted/30',
                  )}
                  aria-pressed={action === value}
                >
                  <span
                    className={cn(
                      'h-4 w-4 shrink-0 rounded-full border-2 flex items-center justify-center transition-colors',
                      action === value ? 'border-foreground' : 'border-muted-foreground/40',
                    )}
                  >
                    {action === value && (
                      <span className="h-2 w-2 rounded-full bg-foreground" />
                    )}
                  </span>
                  <div className="flex-1 min-w-0">
                    <span className="text-[14px] font-medium text-foreground">{title}</span>
                    <p className="text-[12px] text-muted-foreground mt-0.5">{description}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Fight mode: reason chips + evidence */}
          {action === 'fight' && (
            <>
              <div className="space-y-2">
                <label className="text-[13px] font-medium text-foreground">
                  Reason for disputing
                </label>
                <div className="flex flex-wrap gap-2">
                  {FIGHT_REASONS.map((r) => {
                    const isSelected = fightReason === r.value
                    return (
                      <button
                        key={r.value}
                        type="button"
                        onClick={() => setFightReason(r.value)}
                        className={cn(
                          'inline-flex items-center rounded-full px-3.5 py-1.5 text-[13px] font-medium border transition-colors',
                          isSelected
                            ? 'bg-foreground text-background border-foreground'
                            : 'bg-card text-foreground border-input hover:border-foreground/40',
                        )}
                        aria-pressed={isSelected}
                      >
                        {r.label}
                      </button>
                    )
                  })}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[13px] font-medium text-foreground">
                  Evidence{' '}
                  <span className="font-normal text-muted-foreground">(optional)</span>
                </label>
                <textarea
                  value={evidenceDescription}
                  onChange={(e) => setEvidenceDesc(e.target.value)}
                  placeholder="Describe supporting evidence (e.g. tracking number, signed delivery confirmation, communications)..."
                  rows={3}
                  className="w-full rounded-xl border border-input bg-card px-3.5 py-3 text-[14px] resize-none outline-none placeholder:text-muted-foreground/60 focus:border-foreground transition-colors"
                  aria-label="Evidence description"
                />
              </div>
            </>
          )}

          {/* Notes (optional) */}
          <div className="space-y-2">
            <label className="text-[13px] font-medium text-foreground">
              Notes{' '}
              <span className="font-normal text-muted-foreground">(optional)</span>
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any additional context..."
              rows={2}
              className="w-full rounded-xl border border-input bg-card px-3.5 py-3 text-[14px] resize-none outline-none placeholder:text-muted-foreground/60 focus:border-foreground transition-colors"
              aria-label="Additional notes"
            />
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="flex items-center justify-between gap-3 border-t border-border px-6 py-4 bg-card">
          <Button
            type="button"
            variant="outline"
            className="rounded-full px-5"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="action"
            className="rounded-full px-5"
            onClick={handleSubmit}
            disabled={!isValid || isLoading}
          >
            {isLoading
              ? 'Submitting...'
              : action === 'fight'
              ? 'Fight chargeback'
              : 'Accept chargeback'}
          </Button>
        </div>

        </DialogResultTransition>
      </DialogContent>
    </Dialog>
  )
}
